import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop — resets scroll to the top on every route change.
 *
 * Fixes the "blank page" bug: navigating from the bottom of a long page
 * (e.g. /panelist/electronics) to a shorter one (e.g. /panelist/smokers)
 * left the view scrolled past the new page's content.
 *
 * IMPORTANT — the panelist/admin layout (Layout/Admin.js) does NOT scroll
 * the window. On Windows it attaches PerfectScrollbar to the main-panel
 * div and sets `document.body.style.overflow = 'hidden'`, so `window` can
 * never scroll — `window.scrollTo(0,0)` is a guaranteed no-op there. The
 * real scrolling element is `#mainPanel`. We reset both: `window` for any
 * route that scrolls normally (auth/legal pages outside the Admin layout)
 * and `#mainPanel` for the panelist/admin shell.
 *
 * The effect keys off `pathname` only, so it fires on real route
 * navigation — NOT on every render, and NOT on same-route UI changes
 * (modals, accordions, "back to top" buttons).
 *
 * Mount once, as a sibling of the route <Switch>, INSIDE the <Router>
 * (depends on useLocation). Renders nothing.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Routes outside the Admin layout (auth, legal pages) scroll the window.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });

    // The Admin/panelist shell scrolls #mainPanel, not the window.
    // (`id` rather than the JSS class — the class is minified in prod.)
    // Setting scrollTop fires a native 'scroll' event, which PerfectScrollbar
    // listens to — its scrollbar thumb re-syncs automatically.
    const mainPanel = document.getElementById('mainPanel');
    if (mainPanel) {
      mainPanel.scrollTop = 0;
    }
  }, [pathname]);

  return null;
};

export default ScrollToTop;
